import { Fragment } from 'react';

import {
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
} from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { format, parseISO } from 'date-fns';

import {
  Badge,
  BadgeStatus,
  DEFAULT_DATE_FORMAT,
  MoreCircleOutline,
  Partner,
} from '@aslanpay/portal-common-lib';

import { PartnerActions } from 'app/components/UI/Table/Table.types';

import { PartnerActionsIds, PartnerField, partnerActionsValues } from 'app/constants/table';

const partnerStatusDetails: Record<string, { label: string; badgeStatus: BadgeStatus }> = {
  ACTIVE: { label: 'Active', badgeStatus: 'active' },
  INACTIVE: { label: 'Inactive', badgeStatus: 'locked' },
  DELETED: { label: 'Deleted', badgeStatus: 'deleted' },
};

function getPartnerColumns(actions: PartnerActions) {
  const columnHelper = createColumnHelper<Partner>();

  const fields = Object.keys(PartnerField) as Array<keyof typeof PartnerField>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Partner, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof Partner, {
      cell: ({ getValue }) => {
        const value = getValue() as string;

        switch (field) {
          case 'status': {
            const details = partnerStatusDetails[value];
            return details ? (
              <Badge value={details.label} status={details.badgeStatus} />
            ) : (
              <Badge value={value} status="locked" />
            );
          }
          case 'createdAt':
            return value ? format(parseISO(value), DEFAULT_DATE_FORMAT) : '-';
          default:
            return value || '-';
        }
      },
      header: PartnerField[field],
      enableSorting: field !== 'status',
      ...(field === 'status' && {
        meta: { customColumnStyles: { width: '1%', whiteSpace: 'nowrap', paddingRight: '24px' } },
      }),
    });
  });

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<Partner, unknown>) => {
          if (context.column.id === 'options') {
            return { style: { padding: 0, textAlign: 'end' } };
          }
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircleOutline boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {partnerActionsValues.map((actionValue, index) => (
                <Fragment key={actionValue.id}>
                  {index > 0 && <MenuDivider />}
                  <MenuItem
                    color={
                      actionValue.id === PartnerActionsIds.DELETE ? 'system.error' : 'brand.black'
                    }
                    onClick={() =>
                      actions[actionValue.id as keyof PartnerActions](info.row.original)
                    }
                    icon={
                      <Icon
                        as={actionValue.icon}
                        boxSize="14px"
                        color={
                          actionValue.id === PartnerActionsIds.DELETE
                            ? 'system.error'
                            : 'brand.darkGray'
                        }
                      />
                    }
                  >
                    {actionValue.value}
                  </MenuItem>
                </Fragment>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      ),
    })
  );

  return columns;
}

export default getPartnerColumns;
